import { useMyMilestones } from '@/features/milestones/api';
import { useGeeCoinAccount } from '@/features/market/api';
import { Link } from 'react-router';
import { Button } from '@/components/ui/Button';
import { useSignOut } from '@/features/auth/api';
import type { UserProfile } from '@/features/auth/session';
import { ProfileCard } from './ProfileCard';

/**
 * Where the member is reachable.
 *
 * Telegram is real — it is a sign-in method and its group membership is checked
 * on every login. The other four are in the design and behind nothing: there is
 * no OAuth client, no callback, no storage. They are listed so the shape of the
 * screen is right, and marked so nobody taps a button that cannot do anything.
 */
export function SocialAccountsCard({ profile }: { profile: UserProfile }) {
  return (
    <ProfileCard
      title="Sosyal hesaplar"
      description="Topluluk etkinliklerine katılmak için hesaplarını bağla."
    >
      <ul className="flex flex-col divide-y divide-surface-800/70">
        <li className="flex items-center justify-between gap-3 py-3 first:pt-0">
          <span className="flex items-center gap-2.5 text-sm text-surface-200">
            <span aria-hidden className="text-[#2AABEE]">
              <svg viewBox="0 0 24 24" className="size-5 fill-current">
                <path d="M21.9 4.3 18.7 19.4c-.2 1-.9 1.3-1.7.8l-4.7-3.5-2.3 2.2c-.3.3-.5.5-1 .5l.3-4.8 8.7-7.9c.4-.3-.1-.5-.6-.2L6.7 13.2 2 11.7c-1-.3-1-1 .2-1.5l18.4-7.1c.9-.3 1.6.2 1.3 1.2z" />
              </svg>
            </span>
            Telegram
          </span>

          {profile.telegram ? (
            <span className="flex items-center gap-1.5 text-xs text-emerald-400">
              Bağlı
              <svg aria-hidden viewBox="0 0 24 24" className="size-4 fill-current">
                <path d="M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20zm-1 14.4-4-4L8.4 11l2.6 2.6L15.6 9 17 10.4z" />
              </svg>
            </span>
          ) : (
            <span className="text-xs text-surface-600">Bağlı değil</span>
          )}
        </li>

        {(['Kick', 'Discord', 'X (Twitter)', 'Instagram'] as const).map((name) => (
          <li key={name} className="flex items-center justify-between gap-3 py-3 last:pb-0">
            <span className="text-sm text-surface-400">{name}</span>
            <span className="rounded-full bg-surface-800/80 px-2 py-0.5 text-xs font-medium uppercase tracking-wider text-surface-500">
              Yakında
            </span>
          </li>
        ))}
      </ul>
    </ProfileCard>
  );
}

/** Real earned community points, shared with the Market and GeeCoin pages. */
export function GeeCoinCard() {
  const account = useGeeCoinAccount();
  return (
    <ProfileCard title="GeeCoin bakiyesi" description="Etkinliklerden kazan, Market’te kullan.">
      {account.isPending ? (
        <p role="status" className="text-sm text-surface-400">
          Bakiyen yükleniyor…
        </p>
      ) : account.isError ? (
        <div role="alert">
          <p className="text-sm text-surface-400">GeeCoin bakiyen yüklenemedi.</p>
          <Button
            variant="ghost"
            onClick={() => {
              void account.refetch();
            }}
          >
            Tekrar dene
          </Button>
        </div>
      ) : (
        <p className="mb-5 text-3xl font-semibold tracking-tight text-brand-300">
          {new Intl.NumberFormat('tr-TR').format(account.data.balance)}{' '}
          <span className="text-sm text-surface-400">GC</span>
        </p>
      )}
      <div className="mt-4 flex flex-wrap gap-3">
        <Link to="/geecoin" className="gf-button gf-button-secondary">
          Hesap hareketleri ↗
        </Link>
        <Link to="/market" className="gf-button gf-button-ghost">
          Market
        </Link>
      </div>
    </ProfileCard>
  );
}

/** Committed claims and currently claimable milestones are served by Market. */
export function AchievementsCard() {
  const milestones = useMyMilestones();
  return (
    <ProfileCard title="Başarımlar" description="Milestone yolculuğun ve sandıkların.">
      {milestones.isPending ? (
        <p role="status" className="text-sm text-surface-400">
          İlerlemen yükleniyor…
        </p>
      ) : milestones.isError ? (
        <p role="alert" className="text-sm text-surface-400">
          Milestone ilerlemen yüklenemedi.
        </p>
      ) : (
        <div className="flex gap-8">
          <div>
            <strong className="text-2xl text-brand-300">{milestones.data.claimedCount}</strong>
            <p className="mt-1 text-xs text-surface-400">Açılan sandık</p>
          </div>
          <div>
            <strong className="text-2xl text-brand-300">{milestones.data.claimableCount}</strong>
            <p className="mt-1 text-xs text-surface-400">Alınabilir sandık</p>
          </div>
        </div>
      )}
      <Link to="/milestone-rewards" className="gf-button gf-button-secondary mt-5">
        Adaları keşfet ↗
      </Link>
    </ProfileCard>
  );
}

/** Real destinations and the one destructive action. */
export function QuickActionsCard() {
  const signOut = useSignOut();

  return (
    <ProfileCard title="Hızlı işlemler">
      <nav aria-label="Hızlı işlemler">
        <ul className="flex flex-col divide-y divide-surface-800/70">
          <ActionLink to="/wager-races" label="Wager yarışmaları" />
          <ActionLink to="/trusted-sites" label="Güvenilir siteler" />
          <ActionLink to="/promotions" label="Promosyonlar" />
        </ul>
      </nav>

      <Button
        type="button"
        variant="ghost"
        className="mt-4 w-full justify-start px-0 text-red-400 hover:bg-red-500/10 hover:text-red-300"
        loading={signOut.isPending}
        onClick={() => signOut.mutate()}
      >
        <svg
          aria-hidden
          viewBox="0 0 24 24"
          className="size-5 fill-none stroke-current"
          strokeWidth={1.7}
        >
          <path d="M15 17v2a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h7a2 2 0 0 1 2 2v2" />
          <path d="M19 12H9m10 0-3-3m3 3-3 3" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
        Çıkış yap
      </Button>
    </ProfileCard>
  );
}

function ActionLink({ to, label }: { to: string; label: string }) {
  return (
    <li>
      <Link
        to={to}
        className="flex min-h-11 items-center justify-between gap-3 py-3 text-sm text-surface-200 transition hover:text-surface-50"
      >
        {label}
        <svg
          aria-hidden
          viewBox="0 0 24 24"
          className="size-4 shrink-0 fill-none stroke-surface-600"
          strokeWidth={2}
        >
          <path d="m9 6 6 6-6 6" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </Link>
    </li>
  );
}
